import { FunctionComponent, useEffect, useRef, useState, useContext } from "react";
import { useAppSelector } from "./store/hooks";
import { GameState } from "./store/gameStateSlice";
import { ActionsContext, LocalGameActions } from "./ActionsContext";
import { getClientPlayer } from "./Utils";
import { Player, MatchScore } from "./Types";

const TURN_SECONDS = 60;

/**
 * Таймер хода: 60 секунд с момента передачи хода.
 * Отсчёт идёт как при PlayerRolling (нужно бросить кости),
 * так и при PlayerMoving (нужно сделать ход).
 * Если время истекает — автоматически сдаёмся (тайм-аут = проигрыш).
 */
const MoveTimer: FunctionComponent = () => {
  const actions = useContext(ActionsContext);
  const [gameState, players, currentPlayer, gameBoardState, matchScore, doublingCubeData] =
    useAppSelector(s => [
      s.gameState,
      s.players,
      s.currentPlayer,
      s.gameBoard,
      s.matchScore,
      s.doublingCube,
    ]);

  const [secondsLeft, setSecondsLeft] = useState(TURN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const forfeitCalledRef = useRef(false);
  const isLocal = actions instanceof LocalGameActions;

  // Определяем: мой ли сейчас ход (только онлайн, только когда оба игрока подключены)
  const bothConnected = players.playerOne != null && players.playerTwo != null;
  const myPlayer = (!isLocal && bothConnected) ? getClientPlayer(players) : null;
  // Таймер активен при PlayerRolling (ждём бросок) и PlayerMoving (ждём ход).
  // Оба состояния — это время игрока, отведённое ему для совершения действия.
  const isMyTurn =
    (gameState === GameState.PlayerMoving || gameState === GameState.PlayerRolling) &&
    !isLocal &&
    bothConnected &&
    myPlayer !== null &&
    myPlayer === currentPlayer;

  // Запускаем/сбрасываем таймер ТОЛЬКО при смене хода (не при переходе
  // PlayerRolling -> PlayerMoving внутри моего хода — 60 секунд даются на всё).
  useEffect(() => {
    if (!isMyTurn) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setSecondsLeft(TURN_SECONDS);
      forfeitCalledRef.current = false;
      return;
    }

    forfeitCalledRef.current = false;
    setSecondsLeft(TURN_SECONDS);

    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyTurn, currentPlayer]);

  // Когда таймер дошёл до 0 — сдаёмся (проигрываем по тайм-ауту)
  useEffect(() => {
    if (secondsLeft === 0 && isMyTurn && !forfeitCalledRef.current) {
      forfeitCalledRef.current = true;
      const stake = (doublingCubeData?.enabled ? doublingCubeData.gameStakes : 1) * 1;
      const winner: Player = currentPlayer === Player.One ? Player.Two : Player.One;
      const newScore: MatchScore = {
        [Player.One]: matchScore[Player.One] + (winner === Player.One ? stake : 0),
        [Player.Two]: matchScore[Player.Two] + (winner === Player.Two ? stake : 0),
        pointsRequiredToWin: matchScore.pointsRequiredToWin,
      };
      actions.forfeitButtonClicked(gameBoardState, newScore, currentPlayer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  if (!isMyTurn) return null;

  const pct = (secondsLeft / TURN_SECONDS) * 100;
  const urgent = secondsLeft <= 10;

  return (
    <div className={`MoveTimer ${urgent ? "urgent" : ""}`}>
      <svg className="MoveTimer-ring" viewBox="0 0 36 36" width="36" height="36">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5" />
        <circle
          cx="18" cy="18" r="15.9"
          fill="none"
          stroke={urgent ? "#ef5350" : "rgba(255,255,255,0.7)"}
          strokeWidth="2.5"
          strokeDasharray={`${pct} 100`}
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
          style={{ transition: "stroke-dasharray 0.9s linear, stroke 0.3s" }}
        />
      </svg>
      <span className="MoveTimer-val">{secondsLeft}</span>
    </div>
  );
};

export default MoveTimer;
